import React from 'react';
import { flexRender, Table as TanstackTable, Row } from '@tanstack/react-table';
import { FaSort, FaSortAmountDown, FaSortAmountUp } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { NoDataPlaceholder } from './NoDataPlaceholder';
import cn from 'classnames';
import { useHtmlDataTheme } from '../../../services/useHtmlDataTheme';

interface ReusableTableProps<TData> {
  table: TanstackTable<TData>;
  /** True only when there is no data yet (initial load). */
  isLoading: boolean;
  /** Background refetch (e.g. after sort); keeps rows visible via placeholderData. */
  isFetching?: boolean;
  /** Block sort header clicks while a request is in flight (avoids stacked requests). */
  disableSortWhileFetching?: boolean;
  columnsLength: number;
  noDataMessage?: string;
  loadingMessage?: string;
  renderSubComponent?: (props: { row: Row<TData> }) => React.ReactElement;
  isRowExpanded?: (row: Row<TData>) => boolean;
  onRowClick?: (row: TData) => void;
  isRowSelected?: (row: TData) => boolean;
}

const TablePagination = <TData,>({
  table,
  busy = false,
  isDark,
}: {
  table: TanstackTable<TData>;
  busy?: boolean;
  isDark: boolean;
}) => {
  const { t } = useTranslation('Common');
  const pageBtnClass = cn(
    'px-3 py-1 text-xs font-bold border rounded transition-colors disabled:opacity-30',
    isDark
      ? 'border-gray-600 text-gray-200 bg-gray-800 hover:bg-gray-700'
      : 'border-slate-200 text-slate-700 bg-white hover:bg-slate-50'
  );

  return (
    <div
      className={cn(
        'flex items-center justify-between p-4 border-t',
        isDark
          ? 'border-gray-700 bg-gray-800'
          : 'border-slate-200 bg-white'
      )}
    >
      <div
        className={cn(
          'text-xs font-bold',
          isDark ? 'text-gray-400' : 'text-slate-500'
        )}
      >
        {t('Showing')}{' '}
        {table.getState().pagination.pageIndex *
          table.getState().pagination.pageSize +
          1}{' '}
        -{' '}
        {Math.min(
          (table.getState().pagination.pageIndex + 1) *
            table.getState().pagination.pageSize,
          table.getRowCount()
        )}{' '}
        of {table.getRowCount()}
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => table.previousPage()}
          disabled={busy || !table.getCanPreviousPage()}
          className={pageBtnClass}
        >
          {t('Previous')}
        </button>
        <button
          type="button"
          onClick={() => table.nextPage()}
          disabled={busy || !table.getCanNextPage()}
          className={pageBtnClass}
        >
          {t('Next')}
        </button>
      </div>
    </div>
  );
};

export const Table = <TData,>({
  table,
  isLoading,
  isFetching = false,
  disableSortWhileFetching = true,
  columnsLength,
  noDataMessage,
  loadingMessage,
  renderSubComponent,
  isRowExpanded,
  onRowClick,
  isRowSelected,
}: ReusableTableProps<TData>) => {
  const { t } = useTranslation('Common');
  const displayNoDataMessage = noDataMessage ?? t('noDataFound');
  const displayLoadingMessage = loadingMessage ?? t('loading');
  const sortBusy = Boolean(isFetching && disableSortWhileFetching);
  const isDark = useHtmlDataTheme() === 'dark';

  if (isLoading) {
    return (
      <div className="p-20 flex flex-col items-center justify-center gap-4">
        <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
        <span
          className={cn(
            'text-xs font-bold',
            isDark ? 'text-gray-500' : 'text-slate-400'
          )}
        >
          {displayLoadingMessage}
        </span>
      </div>
    );
  }

  const rowHoverClass = isDark ? 'hover:bg-gray-800' : 'hover:bg-slate-50';
  const rowSelectedClass = isDark ? 'bg-blue-900/30' : 'bg-blue-50';
  const rowExpandedClass = isDark ? 'bg-gray-800/80' : 'bg-slate-50/70';

  return (
    <div className="flex flex-col relative">
      {isFetching && !isLoading && (
        <div
          className={cn(
            'pointer-events-none absolute inset-0 z-10 backdrop-blur-[1px] transition-opacity',
            isDark ? 'bg-slate-900/35' : 'bg-white/40'
          )}
          aria-hidden
        />
      )}
      <div
        className={cn(
          'overflow-x-auto',
          isFetching && !isLoading && 'opacity-75 transition-opacity'
        )}
      >
        <table
          className={cn(
            'min-w-full divide-y',
            isDark ? 'divide-gray-700' : 'divide-slate-100'
          )}
        >
          <thead className={isDark ? 'bg-gray-800' : 'bg-slate-50'}>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const col = header.column;
                  const canSort = col.getCanSort();
                  const sorted = col.getIsSorted();
                  return (
                    <th
                      key={header.id}
                      className={cn(
                        'px-6 py-4 text-left text-[10px] font-black uppercase tracking-widest',
                        isDark ? 'text-slate-500' : 'text-slate-400'
                      )}
                    >
                      {header.isPlaceholder ? null : canSort ? (
                        <button
                          type="button"
                          title={sortBusy ? 'Updating…' : 'Sort column'}
                          disabled={sortBusy}
                          className={cn(
                            'group inline-flex items-center gap-1.5 max-w-full text-left font-inherit uppercase tracking-widest text-[10px] font-black',
                            'rounded border-0 bg-transparent p-0 cursor-pointer',
                            'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40',
                            isDark
                              ? 'text-slate-400 hover:text-slate-100'
                              : 'text-slate-500 hover:text-slate-800',
                            sortBusy && 'opacity-50 cursor-wait pointer-events-none'
                          )}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (sortBusy) return;
                            col.getToggleSortingHandler()?.(e);
                          }}
                        >
                          <span className="truncate">
                            {flexRender(
                              col.columnDef.header,
                              header.getContext()
                            )}
                          </span>
                          <span
                            className={cn(
                              'shrink-0 inline-flex transition-colors',
                              isDark
                                ? 'text-slate-500 group-hover:text-slate-300'
                                : 'text-slate-400 group-hover:text-slate-500'
                            )}
                            aria-hidden
                          >
                            {sorted === 'asc' ? (
                              <FaSortAmountUp
                                className={cn(
                                  'h-3.5 w-3.5',
                                  isDark ? 'text-slate-300' : 'text-slate-600'
                                )}
                                aria-hidden
                              />
                            ) : sorted === 'desc' ? (
                              <FaSortAmountDown
                                className={cn(
                                  'h-3.5 w-3.5',
                                  isDark ? 'text-slate-300' : 'text-slate-600'
                                )}
                                aria-hidden
                              />
                            ) : (
                              <FaSort
                                className="h-3.5 w-3.5 opacity-[0.32] transition-opacity group-hover:opacity-70"
                                aria-hidden
                              />
                            )}
                          </span>
                        </button>
                      ) : (
                        flexRender(col.columnDef.header, header.getContext())
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody
            className={cn(
              'divide-y',
              isDark
                ? 'divide-gray-700 bg-gray-900'
                : 'divide-slate-50 bg-white'
            )}
          >
            {table.getRowModel().rows.length > 0 ? (
              table.getRowModel().rows.map((row) => {
                const selected = isRowSelected?.(row.original);
                const expanded = isRowExpanded?.(row);
                return (
                  <React.Fragment key={row.id}>
                    <tr
                      className={cn(
                        'transition-colors',
                        onRowClick && 'cursor-pointer',
                        selected && rowSelectedClass,
                        !selected && rowHoverClass,
                        expanded && rowExpandedClass
                      )}
                      onClick={(e) => {
                        const target = e.target as HTMLElement;
                        if (
                          !target.closest(
                            'button, a, input, select, textarea, [role="menuitem"]'
                          )
                        ) {
                          onRowClick?.(row.original);
                        }
                      }}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td
                          key={cell.id}
                          className={cn(
                            'px-6 py-4 align-middle',
                            isDark && 'text-gray-200'
                          )}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      ))}
                    </tr>
                    {renderSubComponent && expanded && (
                      <tr key={`${row.id}-expanded`} className={rowExpandedClass}>
                        <td
                          colSpan={columnsLength}
                          className="p-0"
                          style={{ maxWidth: 0, overflow: 'hidden' }}
                        >
                          {renderSubComponent({ row })}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            ) : (
              <tr>
                <td colSpan={columnsLength} className="h-64 text-center">
                  <NoDataPlaceholder message={displayNoDataMessage} />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {table.getPageCount() > 0 && (
        <TablePagination table={table} busy={sortBusy} isDark={isDark} />
      )}
    </div>
  );
};